'use client';

import { useId } from 'react';
import mapData from '@/lib/cookie-map-geometry.json';
import Image from '@/components/ui/MediaImage';
import { motion } from 'framer-motion';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { useCookieServiceAllowed } from '@/hooks/useCookieServiceAllowed';

const MAPS_SERVICE_KEY = 'google_maps';
const LINE_COLOR = '#dc2626';
type XY = { x: number; y: number };

interface CookieGatedMapProps {
  children: React.ReactNode;
  className?: string;
  minHeight?: string;
}

/**
 * Google Maps onayı yoksa dotted world-map placeholder.
 * Land dots + arcs share dotted-map Mercator coords (getPin) so the hub stays on Türkiye.
 */
export function CookieGatedMap({ children, className, minHeight = '240px' }: CookieGatedMapProps) {
  const { allowed, openSettings } = useCookieServiceAllowed(MAPS_SERVICE_KEY);
  const { getVariable } = useLanguageVariables();
  const gradId = useId().replace(/:/g, '');

  if (allowed) {
    return <>{children}</>;
  }

  const blockedLabel = getVariable(
    'cookie.maps.blocked',
    'To display the map, please allow functional cookies.'
  );
  const openSettingsLabel = getVariable('cookie.maps.open_settings', 'Open cookie settings');

  const curve = (start: XY, end: XY) => {
    const midX = (start.x + end.x) / 2;
    const midY = Math.min(start.y, end.y) - mapData.height * 0.12;
    return `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${end.y}`;
  };

  return (
    <div
      className={`relative isolate overflow-hidden bg-white ${className ?? 'w-full rounded-xl border border-gray-200'}`}
      style={{ minHeight }}
      role="region"
      aria-label={blockedLabel}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {/* Same viewBox / Mercator space as route overlay */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <Image
          src={mapData.bgUrl}
          width={mapData.width}
          height={mapData.height}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center"
          draggable={false}
        />
        <svg
          viewBox={`0 0 ${mapData.width} ${mapData.height}`}
          className="absolute inset-0 h-full w-full"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <linearGradient id={`cookie-map-grad-${gradId}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop offset="8%" stopColor={LINE_COLOR} stopOpacity="1" />
              <stop offset="92%" stopColor={LINE_COLOR} stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>

          {mapData.routes.map((route, i) => (
            <motion.path
              key={`route-${i}`}
              d={curve(route.start, route.end)}
              fill="none"
              stroke={`url(#cookie-map-grad-${gradId})`}
              strokeWidth="0.35"
              initial={{ pathLength: 0, opacity: 0.35 }}
              animate={{ pathLength: [0, 1], opacity: [0.35, 1, 0.55] }}
              transition={{
                duration: 2.2,
                delay: 0.18 * i,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatDelay: 0.8,
              }}
            />
          ))}

          {mapData.routes.map((route, i) => (
            <circle key={`o-${i}`} cx={route.start.x} cy={route.start.y} r="0.45" fill={LINE_COLOR} opacity="0.75" />
          ))}

          <circle cx={mapData.hub.x} cy={mapData.hub.y} r="0.85" fill={LINE_COLOR} />
          <circle cx={mapData.hub.x} cy={mapData.hub.y} r="0.85" fill={LINE_COLOR} opacity="0.4">
            <animate attributeName="r" from="0.85" to="3.2" dur="1.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" from="0.4" to="0" dur="1.8s" repeatCount="indefinite" />
          </circle>
        </svg>
      </div>

      <div className="absolute inset-0 z-10 flex items-center justify-center px-5 py-8">
        <div className="flex max-w-lg flex-col items-center gap-4 text-center">
          <p className="text-base font-medium leading-snug text-neutral-800 sm:text-lg md:text-xl">
            {blockedLabel}
          </p>
          <button
            type="button"
            onClick={openSettings}
            className="rounded-md border border-neutral-300 bg-white/70 px-4 py-2 text-sm font-medium text-neutral-900 transition hover:border-neutral-400 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-alsero-first/30"
          >
            {openSettingsLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
